/**
 * PDF Uploader Component
 * Handles file selection, validation, and upload
 * Also supports direct LaTeX text input
 */

import React, { useState, useCallback } from 'react';
import { Upload, File, X, AlertCircle, FileText, FileUp } from 'lucide-react';
import { useProcessingStore } from '../store/useProcessingStore';
import { useConfigStore } from '../store/useConfigStore';
import { joinUrl } from '../utils/url';
import axios from 'axios';

type InputMode = 'pdf' | 'latex';

export const PDFUploader: React.FC = () => {
  const [inputMode, setInputMode] = useState<InputMode>('pdf');
  const [file, setFile] = useState<File | null>(null);
  const [latexText, setLatexText] = useState<string>('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { serverUrl, setProcessId, setState, setResult } = useProcessingStore();
  const { isConfigured, getAllConfigurations } = useConfigStore();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('请上传PDF文件');
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('请上传PDF文件');
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setError(null);
  }, []);

  const pollStatus = useCallback(async (processId: string) => {
    let lastUpdateTime: Date | null = null;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(joinUrl(serverUrl, `/api/status/${processId}`));
        const newState = response.data.state;

        // Only update if the new state is newer than the last one (prevent KV eventual consistency issues)
        if (newState && newState.lastUpdated) {
          const newUpdateTime = new Date(newState.lastUpdated);
          if (!lastUpdateTime || newUpdateTime >= lastUpdateTime) {
            setState(newState);
            lastUpdateTime = newUpdateTime;
          }
        } else {
          // Fallback if no timestamp
          setState(newState);
        }

        // Set result when processing is completed
        if (response.data.result) {
          setResult(response.data.result);
        }

        if (newState.currentStep === 'completed' ||
            newState.currentStep === 'error') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Status polling error:', err);
      }
    }, 2000);
  }, [serverUrl, setState, setResult]);

  const handleUpload = useCallback(async () => {
    // Validate input based on mode
    if (inputMode === 'pdf' && !file) {
      setError('请先选择文件');
      return;
    }

    if (inputMode === 'latex' && !latexText.trim()) {
      setError('请输入 LaTeX 内容');
      return;
    }

    if (!isConfigured()) {
      setError('请先配置AI角色');
      return;
    }

    const roleConfigurations = getAllConfigurations();
    if (!roleConfigurations) {
      setError('AI配置无效');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (inputMode === 'pdf' && file) {
        formData.append('pdf', file);
      } else if (inputMode === 'latex') {
        // Create a Blob for LaTeX text input
        const latexBlob = new Blob([latexText], { type: 'text/plain' });
        formData.append('latex', latexBlob, 'input.tex');
      }

      formData.append('config', JSON.stringify({
        roleConfigurations,
        inputMode,
        latexOptions: {
          documentClass: 'article',
          fontSize: 12,
          includeGraphics: true,
        },
      }));

      const response = await axios.post(joinUrl(serverUrl, '/api/process'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.processId) {
        setProcessId(response.data.processId);

        // Start polling for status
        pollStatus(response.data.processId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || '处理失败');
    } finally {
      setUploading(false);
    }
  }, [inputMode, file, latexText, isConfigured, getAllConfigurations, serverUrl, setProcessId, pollStatus]);

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      {/* Mode Toggle */}
      <div className="mb-6 flex justify-center space-x-4">
        <button
          onClick={() => {
            setInputMode('pdf');
            setError(null);
          }}
          className={`
            flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all
            ${inputMode === 'pdf'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }
          `}
          disabled={uploading}
        >
          <FileUp className="w-5 h-5" />
          <span>上传 PDF</span>
        </button>
        <button
          onClick={() => {
            setInputMode('latex');
            setError(null);
          }}
          className={`
            flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all
            ${inputMode === 'latex'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }
          `}
          disabled={uploading}
        >
          <FileText className="w-5 h-5" />
          <span>粘贴 LaTeX</span>
        </button>
      </div>

      {/* PDF Upload Mode */}
      {inputMode === 'pdf' && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-12 text-center transition-all
            ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'}
            ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {!file ? (
            <>
              <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">拖拽PDF文件到此处</h3>
              <p className="text-gray-500 mb-4">或点击选择文件</p>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="pdf-upload"
                disabled={uploading}
              />
              <label
                htmlFor="pdf-upload"
                className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
              >
                选择文件
              </label>
            </>
          ) : (
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <File className="w-8 h-8 text-red-500" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                disabled={uploading}
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* LaTeX Text Input Mode */}
      {inputMode === 'latex' && (
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
          <div className="mb-4 flex items-center space-x-2 text-purple-600">
            <FileText className="w-5 h-5" />
            <h3 className="font-semibold">直接输入 LaTeX 内容</h3>
          </div>
          <textarea
            value={latexText}
            onChange={(e) => {
              setLatexText(e.target.value);
              setError(null);
            }}
            placeholder="在此粘贴 LaTeX 代码...&#10;&#10;例如：&#10;\documentclass{article}&#10;\begin{document}&#10;这是一道数学题...&#10;\end{document}"
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            disabled={uploading}
          />
          <div className="mt-2 text-sm text-gray-500 flex justify-between items-center">
            <span>支持完整的 LaTeX 文档或纯文本内容</span>
            <span>{latexText.length.toLocaleString()} 字符</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      {((inputMode === 'pdf' && file) || (inputMode === 'latex' && latexText.trim())) && !uploading && (
        <button
          onClick={handleUpload}
          className={`
            w-full mt-6 px-6 py-3 text-white rounded-lg font-medium transition-all shadow-lg
            ${inputMode === 'pdf'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
            }
          `}
        >
          开始处理
        </button>
      )}

      {uploading && (
        <div className="mt-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">正在处理...</p>
        </div>
      )}
    </div>
  );
};
